import { useState, ReactNode } from 'react'
import { Command } from 'cmdk'

import { translate } from 'utils/translation'

import SelectPrimitive from '../select-primitive'

import ComboBoxOptions from './components/combobox-options'

export interface ComboOption {
  id: number
  group?: string
  value: string
  icon?: ReactNode
  label: string
  helper?: string
  state?: 'default' | 'disabled'
  tooltipContent?: string
  className?: string
}

interface ComboboxProps {
  label?: string
  helper?: string
  placeholder?: string
  state: 'default' | 'disabled'
  size: 'default' | 'large'
  icon?: ReactNode
  options: ComboOption[]
  initialValue?: ComboOption
  externalControllerInputValue?: string
  optionsAreLoading?: boolean
  useVirtualList?: boolean
  externalControllerSetSelectedOption?: (value: ComboOption | null) => void
  onSelect: (selected: ComboOption | null | undefined) => void
  onChangeInput?: (value: string) => void
  className?: string
  boxClassName?: string
  position?: 'top' | 'bottom'
  clearNumbers?: boolean
  hasError?: boolean
}

const ComboBox = ({
  label,
  helper,
  placeholder,
  state = 'default',
  size = 'default',
  icon,
  options,
  initialValue,
  externalControllerInputValue,
  optionsAreLoading = false,
  useVirtualList = false,
  onSelect,
  onChangeInput,
  externalControllerSetSelectedOption,
  className,
  boxClassName = '',
  position,
  clearNumbers = false,
  hasError = false,
}: ComboboxProps) => {
  const [selectedOption, setSelectedOption] = useState<
    ComboOption | null | undefined
  >(initialValue ?? null)
  const [inputValue, setInputValue] = useState(
    translate(initialValue?.label ?? ''),
  )
  const [considerTypingFiltering, setConsiderTypingFiltering] = useState(false)
  const [showOptions, setShowOptions] = useState(false)

  const handleInputChange = (value: string) => {
    const newValue = clearNumbers ? value.replace(/\d/g, '') : value
    setConsiderTypingFiltering(true)
    !showOptions && setShowOptions(true)
    setInputValue(newValue)
    onChangeInput?.(newValue)
  }

  const handleSelectionChange = (option: ComboOption | null) => {
    setSelectedOption(option)
    externalControllerSetSelectedOption?.(option)
    setInputValue(option?.label ?? '')
    onSelect(option)
    onChangeInput?.(option?.label ?? '')
    setConsiderTypingFiltering(false)
    setShowOptions(false)
  }

  const handleBlur = () => {
    if (inputValue === selectedOption?.label || !inputValue) {
      return
    }

    const newSelectedOption = options.find(
      (option) =>
        option.label.toLowerCase() === inputValue.toLowerCase() &&
        option?.state !== 'disabled',
    )

    if (newSelectedOption) {
      handleSelectionChange(newSelectedOption)
    }
  }

  const filteredOptions = options.filter((option) => {
    if (
      considerTypingFiltering &&
      inputValue &&
      typeof option.label === 'string'
    ) {
      return option.label.toLowerCase().includes(inputValue.toLowerCase())
    }
    return true
  })

  return (
    <SelectPrimitive
      label={label}
      helper={helper}
      placeholder={placeholder}
      state={state}
      size={size}
      icon={icon}
      value={externalControllerInputValue ?? inputValue}
      onValueChange={handleInputChange}
      onBlur={handleBlur}
      className={className}
      boxClassName={boxClassName}
      hasError={hasError}
      open={showOptions}
      onOpenChange={setShowOptions}
    >
      <Command>
        <ComboBoxOptions
          filteredOptions={filteredOptions}
          selectedOption={selectedOption}
          optionsAreLoading={optionsAreLoading}
          useVirtualList={useVirtualList}
          onSelect={handleSelectionChange}
          position={position}
        />
      </Command>
    </SelectPrimitive>
  )
}

export default ComboBox
